/** @format */

import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  InputAdornment,
  Snackbar,
  Box,
  Paper,
  Fade,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { getGradientShade } from "./utils";
import {
  trackEvent,
  trackFormInteraction,
  trackConversion,
  EventNames,
} from "../utils/analytics";
import { useTracking, trackFormElement } from "../utils/trackingHooks";

const StyledContainer = styled(Container)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: theme.spacing(20, 0),
  backgroundColor: "#ffffff",
}));

const StyledForm = styled("form")(({ theme }) => ({
  display: "flex",
  width: "100%",
  maxWidth: 800,
  marginTop: theme.spacing(10),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  flex: 1,
  "& .MuiOutlinedInput-root": {
    borderTopLeftRadius: theme.shape.borderRadius,
    borderBottomLeftRadius: theme.shape.borderRadius,
    borderRadius: 25,
    height: "56px",
    transition: theme.transitions.create([
      "border-color",
      "box-shadow",
      "background-color",
    ]),
    "&:hover, &.Mui-focused": {
      backgroundColor: "transparent",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        borderRadius: "inherit",
        padding: "1px", // Adjust this value to change border thickness
        background: getGradientShade(1),
        mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
        maskComposite: "exclude",
        WebkitMaskComposite: "xor",
        pointerEvents: "none",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(0, 0, 0, 0.23)", // Light gray color
    },
    "&:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline":
      {
        borderColor: "transparent",
      },
  },
}));

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "isValid",
})<{ isValid: boolean }>(({ theme, isValid }) => ({
  height: "56px",
  width: "150px",
  borderRadius: 25,
  background: isValid
    ? "linear-gradient(to right, #FF9250, #FF6473, #FE26A0, #CF34B9, #7852DC, #017AFF)"
    : "#e0e0e0",
  color: isValid ? "#ffffff" : "#757575",
  marginLeft: theme.spacing(1), // Add spacing between input and button
  "&:hover": {
    background: isValid ? getGradientShade(2) : "#d5d5d5",
  },
  transition: "background 0.3s ease, transform 0.2s",
  "&:active": {
    transform: "scale(0.95)",
  },
}));

const SuccessBox = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  paddingTop: theme.spacing(6),
  borderRadius: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
  maxWidth: 800,
  marginTop: theme.spacing(5),
  background: "white",
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    borderRadius: "inherit",
    padding: "3px",
    background: getGradientShade(1),
    mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    maskComposite: "exclude",
    WebkitMaskComposite: "xor",
    pointerEvents: "none",
  },
}));

const GradientIcon = styled(CheckCircleOutlineIcon)(({ theme }) => ({
  fontSize: 80,
  marginBottom: theme.spacing(2),
  background: getGradientShade(1),
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  color: "transparent",
}));

const ContactForm: React.FC = () => {
  // Use our custom tracking hook
  const { trackAction } = useTracking("ContactForm");

  const [email, setEmail] = useState("");
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [subscribeSuccess, setSubscribeSuccess] = useState(false);

  // Track form visibility and engagement time
  const formRef = useRef<HTMLFormElement>(null);
  const [formVisible, setFormVisible] = useState(false);
  const [formEngagementStartTime, setFormEngagementStartTime] =
    useState<Date | null>(null);
  const [totalFormEngagementTime, setTotalFormEngagementTime] = useState(0);

  // Observer to track when form becomes visible
  useEffect(() => {
    // Track component view when rendered
    trackAction("view", { component: "ContactForm" });

    // Track when form is visible in viewport
    const observer = new IntersectionObserver(
      ([entry]) => {
        const isVisible = entry.isIntersecting;
        setFormVisible(isVisible);

        if (isVisible && !formEngagementStartTime) {
          // Start tracking time when form becomes visible
          setFormEngagementStartTime(new Date());
          // Track form exposure
          trackFormInteraction("newsletter_form", "start");
        } else if (!isVisible && formEngagementStartTime) {
          // Calculate engagement time when form leaves viewport
          const endTime = new Date();
          const engagementTime =
            endTime.getTime() - formEngagementStartTime.getTime();
          setTotalFormEngagementTime((prev) => prev + engagementTime);
          setFormEngagementStartTime(null);

          // Track form abandonment if not submitted and user spent time on it
          if (!subscribeSuccess && email.length > 0 && !isValidEmail) {
            trackFormInteraction("newsletter_form", "abandon", {
              time_spent_ms: engagementTime,
              email_valid: isValidEmail,
              email_length: email.length,
            });
          }
        }
      },
      { threshold: 0.5 }
    );

    if (formRef.current) {
      observer.observe(formRef.current);
    }

    return () => {
      if (formRef.current) {
        observer.unobserve(formRef.current);
      }

      // Report total engagement time when component unmounts
      trackEvent("form_total_engagement", {
        form_name: "newsletter_form",
        total_time_ms:
          totalFormEngagementTime +
          (formEngagementStartTime
            ? new Date().getTime() - formEngagementStartTime.getTime()
            : 0),
      });
    };
  }, [
    formEngagementStartTime,
    totalFormEngagementTime,
    subscribeSuccess,
    email,
    isValidEmail,
  ]);

  const validateEmail = (email: string) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(String(email).toLowerCase());
  };

  useEffect(() => {
    const isValid = validateEmail(email);
    setIsValidEmail(isValid);

    // Track email validation status changes
    if (email.length > 0) {
      trackFormElement("newsletter_form", "email", "change", {
        is_valid: isValid,
        length: email.length,
        domain: email.split("@")[1] || "",
      });
    }
  }, [email]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Track form submission attempt
    trackFormInteraction("newsletter_form", "complete", {
      email_valid: isValidEmail,
    });

    if (isValidEmail) {
      setIsSubmitting(true);

      // Track submit button click
      trackEvent(EventNames.BUTTON_CLICK, {
        button_name: "newsletter_subscribe",
        button_location: "contact_form",
        email_domain: email.split("@")[1] || "",
      });

      try {
        const url = "https://api.eon.health/subscribe_email";
        const requestStartTime = new Date().getTime();

        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        });

        const requestDuration = new Date().getTime() - requestStartTime;
        const data = await response.json();

        if (response.ok) {
          // Track successful conversion
          trackConversion(EventNames.CONTACT_FORM_SUBMIT, {
            form_type: "newsletter_subscription",
            email_domain: email.split("@")[1] || "",
            response_time_ms: requestDuration,
          });

          setSubscribeSuccess(true);
          setEmail("");
        } else {
          console.error("Error:", data, response);
          setSnackbarMessage(
            data.message || "An error occurred. Please try again."
          );
          setSnackbarOpen(true);

          // Track form error
          trackEvent(EventNames.FORM_ERROR, {
            form_name: "newsletter_form",
            error_type: "api_error",
            error_message: data.message || "Unknown error",
            http_status: response.status,
            response_time_ms: requestDuration,
          });
        }
      } catch (error) {
        console.error("Error:", error);
        setSnackbarMessage("An error occurred. Please try again.");
        setSnackbarOpen(true);

        // Track network or other errors
        trackEvent(EventNames.FORM_ERROR, {
          form_name: "newsletter_form",
          error_type: "network_error",
          error_message:
            error instanceof Error ? error.message : "Unknown error",
        });
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Track validation error
      trackEvent(EventNames.FORM_ERROR, {
        form_name: "newsletter_form",
        error_type: "validation_error",
        field_name: "email",
        field_value: email,
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    // Track notification close
    trackEvent("notification_close", {
      notification_type: "error",
      message: snackbarMessage,
    });
  };

  return (
    <StyledContainer maxWidth={false}>
      <Typography
        variant="h2"
        align="center"
        gutterBottom
        sx={{ fontWeight: "light" }}
      >
        Thrive as Your Best Self
      </Typography>
      {!subscribeSuccess ? (
        <>
          <Typography
            variant="h6"
            align="center"
            color="textSecondary"
            gutterBottom
          >
            Subscribe to our Newsletter to Learn More About EON.HEALTH
          </Typography>
          <StyledForm ref={formRef} onSubmit={handleSubmit}>
            <StyledTextField
              fullWidth
              placeholder="Your Email Address"
              name="email"
              type="email"
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() =>
                trackFormElement("newsletter_form", "email", "focus")
              }
              onBlur={() =>
                trackFormElement("newsletter_form", "email", "blur", {
                  email_length: email.length,
                  is_valid: isValidEmail,
                })
              }
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {/* You can add an email icon here if desired */}
                  </InputAdornment>
                ),
              }}
            />
            <StyledButton
              type="submit"
              variant="contained"
              disableElevation
              isValid={isValidEmail}
              onClick={() => {
                if (!isValidEmail) {
                  // Track click on disabled button
                  trackEvent("button_disabled_click", {
                    button_name: "newsletter_subscribe",
                    reason: "invalid_email",
                    email_length: email.length,
                  });
                }
              }}
            >
              {isSubmitting ? "Sending..." : "Subscribe"}
            </StyledButton>
          </StyledForm>
        </>
      ) : (
        <Fade in={subscribeSuccess} timeout={800}>
          <SuccessBox>
            <Typography
              variant="h4"
              align="center"
              gutterBottom
              sx={{ fontWeight: "medium" }}
            >
              Thanks for subscribing!
            </Typography>
            <Typography
              variant="body1"
              align="center"
              color="textSecondary"
              paragraph
              sx={{ maxWidth: "80%" }}
            >
              Keep an eye out for emails from us with insights on longevity and
              updates about EON.HEALTH
            </Typography>
          </SuccessBox>
        </Fade>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
      />
    </StyledContainer>
  );
};

export default ContactForm;
